import { useState } from 'react';
import '../css/BrowserUiPreviewPage.css';
import FacebookBrowserModal from './FacebookBrowserModal';
import FacebookBrowserModalV1 from './FacebookBrowserModalV1';
import ExternalBrowserGuideModal from './ExternalBrowserGuideModal';
import BrowserTopBarMock from './BrowserTopBarMock';
import BrowserMenuMock from './BrowserMenuMock';
import {
  IN_APP_BROWSER_APP_NAMES,
  UI_PREVIEW_TITLE,
  UI_PREVIEW_INTRO,
  UI_PREVIEW_APP_HEADING,
  UI_PREVIEW_POPUPS_HEADING,
  UI_PREVIEW_SHOW_POPUP_V1_LABEL,
  UI_PREVIEW_SHOW_POPUP_LABEL,
  UI_PREVIEW_SHOW_GUIDE_LABEL,
  UI_PREVIEW_POPUP_NOTE,
  UI_PREVIEW_PICTURES_HEADING,
} from '../constants/bookingConst';

const PREVIEW_VIEWS = { POPUP_V1: 'popupV1', POPUP: 'popup', GUIDE: 'guide' };

/**
 * Preview page for the in-app browser UI, at UI_PREVIEW_ROUTE_PATH.
 *
 * Renders the real components — both versions of the "Open in Your Browser" popup, the
 * "Open in Browser Manually" guide, and the guide's two pictures — in any
 * browser, with a Facebook / Messenger switch, so they can be checked on
 * localhost or a laptop instead of redeploying and opening the site from
 * Facebook. Reads no data, so it needs no sign-in or spinner.
 *
 * @returns {JSX.Element}
 */
function BrowserUiPreviewPage() {
  const [appName, setAppName] = useState(IN_APP_BROWSER_APP_NAMES.FACEBOOK);
  const [openView, setOpenView] = useState(null);

  /** Closes whichever popup is showing. */
  const closeView = () => setOpenView(null);

  return (
    <main className="bup-page">
      <h1 className="bup-title">{UI_PREVIEW_TITLE}</h1>
      <p className="bup-intro">{UI_PREVIEW_INTRO}</p>

      <section className="bup-section">
        <h2 className="bup-heading">{UI_PREVIEW_APP_HEADING}</h2>
        <div className="bup-toggle" role="group" aria-label={UI_PREVIEW_APP_HEADING}>
          {Object.values(IN_APP_BROWSER_APP_NAMES).map((name) => (
            <button
              key={name}
              type="button"
              className={`bup-toggle-option${name === appName ? ' bup-toggle-option--active' : ''}`}
              aria-pressed={name === appName}
              onClick={() => setAppName(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </section>

      <section className="bup-section">
        <h2 className="bup-heading">{UI_PREVIEW_POPUPS_HEADING}</h2>
        <div className="bup-actions">
          <button type="button" className="bup-button" onClick={() => setOpenView(PREVIEW_VIEWS.POPUP_V1)}>
            {UI_PREVIEW_SHOW_POPUP_V1_LABEL}
          </button>
          <button type="button" className="bup-button" onClick={() => setOpenView(PREVIEW_VIEWS.POPUP)}>
            {UI_PREVIEW_SHOW_POPUP_LABEL}
          </button>
          <button type="button" className="bup-button bup-button--secondary" onClick={() => setOpenView(PREVIEW_VIEWS.GUIDE)}>
            {UI_PREVIEW_SHOW_GUIDE_LABEL}
          </button>
        </div>
        <p className="bup-note">{UI_PREVIEW_POPUP_NOTE}</p>
      </section>

      <section className="bup-section">
        <h2 className="bup-heading">{UI_PREVIEW_PICTURES_HEADING}</h2>
        <div className="bup-pictures">
          <BrowserTopBarMock appName={appName} host={window.location.host} />
          <BrowserMenuMock />
        </div>
      </section>

      {openView === PREVIEW_VIEWS.POPUP_V1 && (
        <FacebookBrowserModalV1 onClose={closeView} onOpen={closeView} />
      )}
      {openView === PREVIEW_VIEWS.POPUP && (
        <FacebookBrowserModal preview appName={appName} onClose={closeView} />
      )}
      {openView === PREVIEW_VIEWS.GUIDE && (
        <ExternalBrowserGuideModal appName={appName} onClose={closeView} />
      )}
    </main>
  );
}

export default BrowserUiPreviewPage;
